import { format } from 'date-fns';
import { capitalize, uniqBy } from 'lodash';

import { GOAL_STATUS_NODES, ONE_ON_ONE_STATUS_NODES } from '../../constants';
import { METRIC_RESPONSE, METRIC_TYPE_TRANSFORMERS } from '../types/MetricsTypes';

const convertGoalsByStatusData = (data: METRIC_RESPONSE, maxXAxisTicksCount = null) => {
  let xAxis = data?.data.points.map((point) =>
    point.dimensions.find((dim) => dim.name === 'employee' || dim.name === 'Organization_Hierarchy')
  );
  xAxis = uniqBy(xAxis, 'raw');

  if (maxXAxisTicksCount && xAxis.length > maxXAxisTicksCount) {
    xAxis = xAxis.slice(0, maxXAxisTicksCount);
  }

  const goalCountByStatusForEmployee: { [key: string]: { [key: string]: number[] } } = {};

  data?.data.points.forEach((point) => {
    const employeeId = point.dimensions.find(
      (dim) => dim.name === 'employee' || dim.name === 'Organization_Hierarchy'
    )?.raw;
    const goalStatus = point.dimensions.find((dim) => dim.name === 'goal_status')?.raw;

    if (employeeId && goalStatus) {
      if (!goalCountByStatusForEmployee[employeeId]) {
        goalCountByStatusForEmployee[employeeId] = {};
      }

      if (!goalCountByStatusForEmployee[employeeId][goalStatus]) {
        goalCountByStatusForEmployee[employeeId][goalStatus] = [];
      }

      goalCountByStatusForEmployee[employeeId][goalStatus].push(Number(point.value));
    }
  });

  const series = [
    {
      id: 'ON_TRACK',
      data: [],
    },
    {
      id: 'OFF_TRACK',
      data: [],
    },
    {
      id: 'AT_RISK',
      data: [],
    },
    // {
    //   id: 'ACHIEVED',
    //   data: [],
    // },
    // {
    //   id: 'NOT_ACHIEVED',
    //   data: [],
    // },
    {
      id: 'CLOSED',
      data: [],
    },
  ];

  xAxis.forEach((x) => {
    series.forEach((s) => {
      const scores = goalCountByStatusForEmployee[x.raw]?.[s.id];
      if (scores) {
        const averageScore = scores.reduce((sum, val) => sum + val, 0) / scores.length;
        s.data.push(Math.round(averageScore));
      } else {
        s.data.push(null);
      }
    });
  });

  return {
    nodes: GOAL_STATUS_NODES,
    xAxis: xAxis.map((x) => x.display),
    series,
  };
};

const convertOneOnOneByStatusData = (data: METRIC_RESPONSE, maxXAxisTicksCount = null) => {
  let xAxis = data?.data.points.map((point) =>
    point.dimensions.find((dim) => dim.name === 'employee' || dim.name === 'Organization_Hierarchy')
  );
  xAxis = uniqBy(xAxis, 'raw');

  if (maxXAxisTicksCount && xAxis.length > maxXAxisTicksCount) {
    xAxis = xAxis.slice(0, maxXAxisTicksCount);
  }

  const oneOnOneCountByStatusForEmployee: { [key: string]: { [key: string]: number[] } } = {};

  data?.data.points.forEach((point) => {
    const employeeId = point.dimensions.find(
      (dim) => dim.name === 'employee' || dim.name === 'Organization_Hierarchy'
    )?.raw;
    const oneOnOneStatus = point.dimensions.find(
      (dim) => dim.name === 'One_On_One_Occurrence_Event_v2.One_On_One_Occurrence_Status_LevelDim'
    )?.raw;

    if (employeeId && oneOnOneStatus) {
      if (!oneOnOneCountByStatusForEmployee[employeeId]) {
        oneOnOneCountByStatusForEmployee[employeeId] = {};
      }

      if (!oneOnOneCountByStatusForEmployee[employeeId][oneOnOneStatus]) {
        oneOnOneCountByStatusForEmployee[employeeId][oneOnOneStatus] = [];
      }

      oneOnOneCountByStatusForEmployee[employeeId][oneOnOneStatus].push(Number(point.value));
    }
  });

  const series = [
    {
      id: 'COMPLETED',
      data: [],
    },
    {
      id: 'CANCELLED',
      data: [],
    },
  ];

  xAxis.forEach((x) => {
    series.forEach((s) => {
      const scores = oneOnOneCountByStatusForEmployee[x.raw]?.[s.id];
      if (scores) {
        const averageScore = scores.reduce((sum, val) => sum + val, 0) / scores.length;
        s.data.push(Math.round(averageScore));
      } else {
        s.data.push(null);
      }
    });
  });

  return {
    xAxis: xAxis.map((x) => x.display),
    series,
    nodes: ONE_ON_ONE_STATUS_NODES,
  };
};

const convertOneOnOneTopicDistribution = (data: METRIC_RESPONSE) => {
  let topics = data?.data.points.map((point) =>
    point.dimensions.find((dim) => dim.name === 'One_On_One_Occurrence_Agenda.Agenda_Theme_LevelDim')
  );
  topics = uniqBy(topics, 'raw');
  const nodes = topics.map((node) => ({
    id: node.raw,
    name: capitalize(node.display),
  }));

  // getting months from point name === "date"
  const months = data?.data.points.map((point) => point.dimensions.find((dim) => dim.name === 'date').display);
  let xAxis = months.map((month) => ({
    value: month,
    display: format(new Date(month), 'MMM'),
  }));
  xAxis = uniqBy(xAxis, 'value');

  const series = nodes.map((node) => ({
    id: node.id,
    data: [],
  }));

  xAxis.forEach((x) => {
    series.forEach((s) => {
      const point = data?.data.points.find(
        (p) =>
          p.dimensions.find((dim) => dim.name === 'date').display === x.value &&
          p.dimensions.find((dim) => dim.name === 'One_On_One_Occurrence_Agenda.Agenda_Theme_LevelDim').raw === s.id
      );
      const valueConvertedToPercentage = point?.value ?? null;
      s.data.push(valueConvertedToPercentage);
    });
  });

  return {
    nodes,
    xAxis: xAxis.map((x) => x.display),
    series,
  };
};

const convertEngagementSurveyReportLineChart = (
  data: METRIC_RESPONSE,
  timelineDateRange: { startDate: string; endDate: string }
) => {
  let topics = data?.data.points.map(
    (point) => point.dimensions.find((dim) => dim.name === 'Question Description') || {}
  );

  topics = uniqBy(topics, 'raw');
  const nodes = topics.map((node) => ({
    id: node.raw,
    name: capitalize(node.display),
  }));

  const months = data?.data.points.map(
    (point) => point.dimensions.find((dim) => dim.name === 'Submission Time')?.display
  );

  let xAxis = months.map((month) => ({
    value: month,
    display: format(new Date(month), 'MMM, yy'),
  }));

  xAxis = uniqBy(xAxis, 'value');
  xAxis = xAxis.sort((a, b) => new Date(a.value).getTime() - new Date(b.value).getTime());

  const series = nodes.map((node) => ({
    id: node.id,
    data: [],
  }));

  xAxis.forEach((x) => {
    series.forEach((s) => {
      const point = data?.data.points.find(
        (p) =>
          p.dimensions.find((dim) => dim.name === 'Submission Time')?.display === x.value &&
          p.dimensions.find((dim) => dim.name === 'Question Description')?.raw === s.id
      );
      const valueConvertedToPercentage = point?.value ?? null;
      s.data.push(valueConvertedToPercentage);
    });
  });

  // calculate the xAxisDomain from the startDate and endDate
  const startDate = new Date(timelineDateRange.startDate);
  const endDate = new Date(timelineDateRange.endDate);
  const xAxisDomain = [];

  for (let date = startDate; date <= endDate; date.setMonth(date.getMonth() + 1)) {
    xAxisDomain.push(format(date, 'MMM, yy'));
  }

  return {
    nodes,
    xAxis: xAxis.map((x) => x.display),

    xAxisDomain,

    series,
  };
};

const convertCompetencyScoreByAverage = (data: METRIC_RESPONSE) => {
  let nodesData = data?.data?.points.map((point) =>
    point.dimensions.find((dim) => dim.name === 'm3s11_Competency_Subject.Competency_Name_LevelDim')
  );
  nodesData = uniqBy(nodesData, 'raw');

  let xAxisData = data?.data?.points.map((point) =>
    point.dimensions.find((dim) => dim.name === 'employee' || dim.name === 'Organization_Hierarchy')
  );
  xAxisData = uniqBy(xAxisData, 'raw');

  const competencyScoreByAverage: { [key: string]: { [key: string]: number[] } } = {};

  data?.data?.points.forEach((point) => {
    const competencyId = point.dimensions.find(
      (dim) => dim.name === 'm3s11_Competency_Subject.Competency_Name_LevelDim'
    )?.raw;
    const xAxisId = point.dimensions.find(
      (dim) => dim.name === 'employee' || dim.name === 'Organization_Hierarchy'
    )?.raw;
    if (competencyId && xAxisId) {
      if (!competencyScoreByAverage[competencyId]) {
        competencyScoreByAverage[competencyId] = {};
      }
      if (!competencyScoreByAverage[competencyId][xAxisId]) {
        competencyScoreByAverage[competencyId][xAxisId] = [];
      }
      competencyScoreByAverage[competencyId][xAxisId].push(Number(point.value));
    }
  });

  const nodes = nodesData?.map((x) => ({ id: x?.raw, name: x?.display ?? null }));

  const series = nodes.map((node) => ({
    id: node.id,
    data: xAxisData.map((x) => {
      const scores = competencyScoreByAverage[node.id][x.raw];
      if (scores) {
        const averageScore = scores.reduce((sum, val) => sum + val, 0) / scores.length;
        return Math.round(averageScore);
      }
      return null;
    }),
  }));

  const xAxis = xAxisData.map((x) => x.display);

  return {
    xAxis,
    series,
    nodes,
  };
};

const convertCompetencyProgressByAverage = (data: METRIC_RESPONSE, maxUserDisplayCount = null) => {
  let employees = data?.data.points.map((point) =>
    point.dimensions.find((dim) => dim.name === 'employee' || dim.name === 'Organization_Hierarchy')
  );
  employees = uniqBy(employees, 'raw');

  if (maxUserDisplayCount && employees.length > maxUserDisplayCount) {
    employees = employees.slice(0, maxUserDisplayCount);
  }

  const nodes = employees.map((node) => ({
    id: node.raw,
    name: node.display,
  }));

  // getting months from point name === "date"
  const months = data?.data.points.map((point) => point.dimensions.find((dim) => dim.name === 'date').display);
  let xAxis = months.map((month) => ({
    value: month,
    display: format(new Date(month), 'MMM'),
  }));
  xAxis = uniqBy(xAxis, 'value');

  const series = nodes.map((node) => ({
    id: node.id,
    data: [],
  }));

  xAxis.forEach((x) => {
    series.forEach((s) => {
      const point = data?.data.points.find(
        (p) =>
          p.dimensions.find((dim) => dim.name === 'date').display === x.value &&
          p.dimensions.find((dim) => dim.name === 'employee' || dim.name === 'Organization_Hierarchy').raw === s.id
      );
      const valueConvertedToPercentage = point?.value ? Number(point.value).toFixed(0) : null;
      s.data.push(valueConvertedToPercentage);
    });
  });

  return {
    nodes,
    xAxis: xAxis.map((x) => x.display),
    series,
  };
};

const convertGoalProgressData = (data: METRIC_RESPONSE, maxUserDisplayCount = null) => {
  let employees = data?.data.points.map((point) =>
    point.dimensions.find((dim) => dim.name === 'employee' || dim.name === 'Organization_Hierarchy')
  );

  employees = uniqBy(employees, 'raw');

  if (maxUserDisplayCount && employees.length > maxUserDisplayCount) {
    employees = employees.slice(0, maxUserDisplayCount);
  }

  const nodes = employees.map((node) => ({
    id: node.raw,
    name: node.display,
  }));

  // getting months from point name === "date"
  const months = data?.data.points.map((point) => point.dimensions.find((dim) => dim.name === 'date').display);
  let xAxis = months.map((month) => ({
    value: month,
    display: format(new Date(month), 'MMM'),
  }));
  xAxis = uniqBy(xAxis, 'value');

  const series = nodes.map((node) => ({
    id: node.id,
    data: [],
  }));

  xAxis.forEach((x) => {
    series.forEach((s) => {
      const point = data?.data.points.find(
        (p) =>
          p.dimensions.find((dim) => dim.name === 'date').display === x.value &&
          p.dimensions.find((dim) => dim.name === 'employee' || dim.name === 'Organization_Hierarchy').raw === s.id
      );
      const valueConvertedToPercentage = point?.value ? (Number(point.value) * 100).toFixed(0) : null;
      s.data.push(valueConvertedToPercentage);
    });
  });

  return {
    nodes,
    xAxis: xAxis.map((x) => x.display),
    series,
  };
};

const convertGoalProgressKPI = (data: METRIC_RESPONSE) => {
  const points = data?.data?.points;
  const hasValidPoints = Array.isArray(points) && points.length > 0;

  const averageProgress = hasValidPoints ? (Number(points[0]?.value) * 100).toFixed(2) : 0;

  return {
    averageProgress: Number(averageProgress),
  };
};

const convertMetricCardData = (data: METRIC_RESPONSE) => ({ data: Number(data?.data) });

const convertDisciplinaryActionsNoteCountData = (data: METRIC_RESPONSE) => {
  let topics = data?.data.points.map((point) => point.dimensions.find((dim) => dim.name === 'Note Type') || {});

  topics = uniqBy(topics, 'raw');
  const nodes = topics.map((node) => ({
    id: node.raw,
    name: capitalize(node.display),
  }));

  const months = data?.data.points.map((point) => point.dimensions.find((dim) => dim.name === 'Month')?.display);
  let xAxis = months.map((month) => ({
    value: month,
    display: format(new Date(month), 'MMM'),
  }));

  xAxis = uniqBy(xAxis, 'value');
  xAxis = xAxis.sort((a, b) => new Date(a.value).getTime() - new Date(b.value).getTime());

  let series = nodes.map((node) => ({
    id: node.id,
    data: [],
  }));

  series = series?.filter((node) => !!node.id);

  xAxis.forEach((x) => {
    series.forEach((s) => {
      const point = data?.data.points.find(
        (p) =>
          p.dimensions.find((dim) => dim.name === 'Month')?.display === x.value &&
          p.dimensions.find((dim) => dim.name === 'Note Type')?.raw === s.id
      );
      s.data.push(point?.value ?? null);
    });
  });

  return {
    nodes,
    xAxis: xAxis?.map((x) => x.display),
    series,
  };
};

const convertDisciplinaryActionsNoteStageData = (data: METRIC_RESPONSE) => {
  let topics = data?.data?.points?.map((point) => point.dimensions.find((dim) => dim.name === 'status') || {});

  topics = uniqBy(topics, 'raw');
  const nodes = topics?.map((node) => ({
    id: node.raw,
    name: capitalize(node.display),
  }));

  const stages = data?.data?.points?.map((point) => point.dimensions.find((dim) => dim.name === 'stage')?.display);
  let xAxis = stages?.map((stage) => ({
    value: stage,
    display: stage,
  }));

  xAxis = uniqBy(xAxis, 'value');

  let series = nodes?.map((node) => ({
    id: node.id,
    data: [],
  }));

  series = series?.filter((node) => !!node.id);

  xAxis.forEach((x) => {
    series.forEach((s) => {
      const point = data?.data.points.find(
        (p) =>
          p.dimensions.find((dim) => dim.name === 'stage')?.display === x.value &&
          p.dimensions.find((dim) => dim.name === 'status')?.raw === s.id
      );
      s.data.push(point?.value ?? null);
    });
  });

  return {
    nodes,
    xAxis: xAxis?.map((x) => x.display),
    series,
  };
};

const convertDisciplinaryActionsAdminReportEmployeeTrendBarChart = (data: METRIC_RESPONSE) => {
  const points = data?.data.points || [];

  return points
    .map((point) => {
      const monthDim = point.dimensions.find((dim) => dim.name === 'Month');
      if (!monthDim || !point.value) return null;

      return {
        x: format(new Date(monthDim.display), 'MMM yyyy'), // Format "2024-10-01" to "Oct 2024"
        y: parseFloat(point.value),
      };
    })
    .filter((item) => item !== null);
};

const convertDisciplinaryActionsAdminReportViolationCountLineChart = (data: METRIC_RESPONSE) => {
  const points = data?.data.points || [];
  const nodes = [
    {
      id: data.metric,
      name: 'Employees with Violations',
    },
  ];
  const xAxis = points.map((point) => point.dimensions[0].display);
  const series = [
    {
      id: data.metric,
      data: points.map((point) => (point.value ? parseFloat(point.value) : null)),
    },
  ];

  return {
    nodes,
    xAxis,
    series,
  };
};

export const metricTypeTransformers: METRIC_TYPE_TRANSFORMERS = {
  GOAL_COUNT_BY_STATUS: convertGoalsByStatusData,
  ENGAGEMENT_SCORE: convertEngagementSurveyReportLineChart,
  ACTIVE_GOAL_PROGRESS_AVERAGE: convertGoalProgressData,
  ACTIVE_GOAL_PROGRESS_AVERAGE_KPI: convertGoalProgressKPI,
  COMPETENCY_PROGRESS_AVERAGE: convertCompetencyProgressByAverage,
  ONE_ON_ONE_COUNT_BY_STATUS: convertOneOnOneByStatusData,
  COMPETENCY_SCORE_AVERAGE_BY_COMPETENCY: convertCompetencyScoreByAverage,
  ONE_ON_ONE_COMPLETION_PERCENTAGE_AVERAGE_KPI: convertGoalProgressKPI,
  EMP_COUNT_NO_OOO_SCHEDULED: convertMetricCardData,
  EMP_COUNT_NO_OOO_COMPLETED: convertMetricCardData,
  COMPETENCY_PROGRESS_AVERAGE_KPI: convertGoalProgressKPI,
  INDIVIDUAL_COMPETENCIES_NO_PROGRESS: convertMetricCardData,
  INDIVIDUAL_FEEDBACK_RECEIVED: convertMetricCardData,
  EMP_COUNT_COMPETENCIES_NO_PROGRESS: convertMetricCardData,
  EMP_COUNT_NO_FEEDBACK_RECEIVED: convertMetricCardData,
  EMP_COUNT_NO_GOALS_SET: convertMetricCardData,
  EMP_COUNT_NO_GOAL_CHECKINS: convertMetricCardData,
  EMP_COUNT_NO_FEEDBACK_SHARED: convertMetricCardData,
  EMP_COUNT_5_GOALS_OR_MORE: convertMetricCardData,
  MANAGER_COUNT_NO_OOOS_WITH_REPORTS: convertMetricCardData,
  ONE_ON_ONE_SATISFACTION_SCORE_AVERAGE_KPI: convertGoalProgressKPI,
  ONE_ON_ONE_AGENDA_TOPIC_DISTRIBUTION: convertOneOnOneTopicDistribution,
  DISCIPLINARY_NOTES_COUNT: convertDisciplinaryActionsNoteCountData,
  DISCIPLINARY_NOTES_STAGE_COUNT: convertDisciplinaryActionsNoteStageData,
  UNIQUE_EMPLOYEES_WITH_DISCIPLINARY_NOTES: convertDisciplinaryActionsAdminReportEmployeeTrendBarChart,
  EMPLOYEES_WITH_VIOLATIONS_PERCENTAGE: convertDisciplinaryActionsAdminReportViolationCountLineChart,
};
